import type { AiTextKind } from '@pulsify/shared/dto'
import { Sparkles } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGenerateText } from '~/features/ai-studio/hooks'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Textarea } from '~/shared/ui/textarea'

interface AiTextCopy {
  description: () => string
  promptLabel: () => string
  promptPlaceholder: () => string
}

const POST_COPY: AiTextCopy = {
  description: m.aiTextPostDescription,
  promptLabel: m.aiTextPostPromptLabel,
  promptPlaceholder: m.aiTextPostPromptPlaceholder,
}

const COPY: Record<AiTextKind, AiTextCopy> = {
  'facebook-post': POST_COPY,
  'instagram-post': POST_COPY,
  'event-description': {
    description: m.aiTextEventDescription,
    promptLabel: m.aiTextEventPromptLabel,
    promptPlaceholder: m.aiTextEventPromptPlaceholder,
  },
  'place-description': {
    description: m.aiTextPlaceDescription,
    promptLabel: m.aiTextPlacePromptLabel,
    promptPlaceholder: m.aiTextPlacePromptPlaceholder,
  },
}

export function AiTextButton({ kind, onUse }: { kind: AiTextKind; onUse: (text: string) => void }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Sparkles className="h-4 w-4" />
        {m.aiTextButton()}
      </Button>
      <AiTextDialog kind={kind} open={open} onOpenChange={setOpen} onUse={onUse} />
    </>
  )
}

function AiTextDialog({
  kind,
  open,
  onOpenChange,
  onUse,
}: {
  kind: AiTextKind
  open: boolean
  onOpenChange: (open: boolean) => void
  onUse: (text: string) => void
}) {
  const { data: business } = useCurrentBusiness()
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState<string | null>(null)
  const promptId = useId()
  const generate = useGenerateText(business?.id ?? '')
  const copy = COPY[kind]

  const handleGenerate = () => {
    generate.mutate(
      { kind, prompt },
      {
        onSuccess: ({ text }) => setResult(text),
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!generate.isPending) onOpenChange(next)
      }}
    >
      <DialogContent showCloseButton={!generate.isPending}>
        <DialogHeader>
          <DialogTitle>{m.aiTextTitle()}</DialogTitle>
          <DialogDescription>{copy.description()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor={promptId}>{copy.promptLabel()}</Label>
          <Textarea
            id={promptId}
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={copy.promptPlaceholder()}
          />
        </div>

        {result !== null && (
          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border bg-muted/40 p-3 text-sm">
            {result}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleGenerate}
            disabled={generate.isPending || !business || prompt.trim().length === 0}
          >
            {generate.isPending ? (
              <PulsifyLoader className="size-5" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {result === null ? m.aiGenerate() : m.aiRegenerate()}
          </Button>
          {result !== null && (
            <Button
              type="button"
              onClick={() => {
                onUse(result)
                onOpenChange(false)
              }}
            >
              {m.aiTextUse()}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
